import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, unref as _unref, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex items-center justify-center py-24"
}
const _hoisted_2 = { class: "mb-8" }
const _hoisted_3 = { class: "text-2xl font-semibold text-gray-800" }
const _hoisted_4 = { class: "text-normal text-gray-400 mt-1" }
const _hoisted_5 = { class: "bg-white rounded-xl border border-gray-200 p-6 space-y-3" }
const _hoisted_6 = { class: "grid grid-cols-2 gap-3" }
const _hoisted_7 = { class: "mt-6 flex justify-end" }
const _hoisted_8 = { class: "w-40" }
const _hoisted_9 = { class: "mt-8" }
const _hoisted_10 = { class: "text-base font-semibold text-gray-800 mb-1" }
const _hoisted_11 = { class: "text-sm text-gray-400 mb-4" }
const _hoisted_12 = { class: "bg-white rounded-xl border border-gray-200 p-6" }
const _hoisted_13 = { class: "w-64" }
const _hoisted_14 = { class: "block text-sm font-medium text-gray-700 mb-1" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["value"]
const _hoisted_17 = {
  key: 0,
  class: "mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
}
const _hoisted_18 = { class: "mt-8" }
const _hoisted_19 = { class: "bg-white rounded-xl border border-red-200 p-6" }
const _hoisted_20 = {
  key: 0,
  class: "flex items-start justify-between gap-4 flex-wrap"
}
const _hoisted_21 = { class: "text-base font-semibold text-red-600" }
const _hoisted_22 = { class: "text-sm text-gray-400 mt-0.5" }
const _hoisted_23 = { class: "w-44 flex-shrink-0" }
const _hoisted_24 = { class: "flex items-start gap-3" }
const _hoisted_25 = { class: "text-sm font-semibold text-red-600" }
const _hoisted_26 = { class: "text-sm text-gray-600 mt-1" }
const _hoisted_27 = { class: "flex justify-end gap-2 mt-5" }
const _hoisted_28 = { class: "w-24" }
const _hoisted_29 = { class: "w-44" }
const _hoisted_30 = { class: "text-base font-semibold text-red-600" }
const _hoisted_31 = { class: "text-sm text-gray-500 mb-4 mt-1" }
const _hoisted_32 = { class: "w-56" }
const _hoisted_33 = {
  key: 0,
  class: "text-xs text-error-500"
}
const _hoisted_34 = { class: "flex items-center justify-between pt-1" }
const _hoisted_35 = ["disabled"]
const _hoisted_36 = { class: "flex gap-2" }
const _hoisted_37 = { class: "w-24" }
const _hoisted_38 = { class: "w-60" }

import { reactive, ref, computed, watch, onUnmounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { Info, AlertTriangle } from 'lucide-vue-next';
import AppInput from '@/components/ui/AppInput.vue';
import AppButton from '@/components/ui/AppButton.vue';
import { useStore } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { securityApi } from '@/api/security';
import Authentication from '@/plugins/authentication';


export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const store = useStore();
const toast = useToast();
const { t } = useI18n();
const loading = ref(!store.state.user);
const saving = ref(false);
const languageSaving = ref(false);
const showLanguageChangedNotice = ref(false);

const form = reactive({ firstName: '', lastName: '', email: '' });
const savedForm = reactive({ firstName: '', lastName: '' });
const selectedLanguageId = ref<number | null>(null);

const supportedLanguages = computed(() => store.state.languages.filter((l) => l.is_supported));
const hasChanged = computed(
    () => form.firstName !== savedForm.firstName || form.lastName !== savedForm.lastName,
);

// Populate form reactively — AccountLayout already fetches the profile
const storeUser = computed(() => store.state.user);
watch(storeUser, (user) => {
    if (user) {
        form.firstName = user.firstName ?? '';
        form.lastName = user.lastName ?? '';
        form.email = user.email ?? '';
        savedForm.firstName = form.firstName;
        savedForm.lastName = form.lastName;
        selectedLanguageId.value = user.languageId ?? null;
        loading.value = false;
    }
}, { immediate: true });


async function save() {
    saving.value = true;
    try {
        await store.dispatch(ActionType.UpdateProfile, {
            firstName: form.firstName,
            lastName: form.lastName,
        });
        savedForm.firstName = form.firstName;
        savedForm.lastName = form.lastName;
        toast.success(t('account.profile.saved'));
    } catch {
        toast.error(t('errors.unknown'));
    } finally {
        saving.value = false;
    }
}

async function onLanguageChange() {
    if (selectedLanguageId.value === null) return;
    languageSaving.value = true;
    try {
        await store.dispatch(ActionType.SetLanguage, selectedLanguageId.value);
        showLanguageChangedNotice.value = true;
    } catch {
        toast.error(t('errors.unknown'));
    } finally {
        languageSaving.value = false;
    }
}

// ── Delete account (danger zone) ─────────────────────────────────
// Mirrors the two-step verification-code flow in security/Index.vue.
const delStep = ref<'idle' | 'warning' | 'code_sent'>('idle');
const delForm = reactive({ code: '' });
const delErrors = reactive({ code: '', general: '' });
const delSaving = ref(false);
const resendCooldown = ref(0);
let resendTimer: ReturnType<typeof setInterval> | null = null;

function startResendCooldown() {
    resendCooldown.value = 60;
    resendTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0 && resendTimer) {
            clearInterval(resendTimer);
            resendTimer = null;
        }
    }, 1000);
}

// Cancel commits to nothing: reset to idle, clear all state, drop the cooldown.
function cancelDeletion() {
    delStep.value = 'idle';
    delForm.code = '';
    delErrors.code = '';
    delErrors.general = '';
    if (resendTimer) { clearInterval(resendTimer); resendTimer = null; }
    resendCooldown.value = 0;
}

async function requestDeletionCode() {
    delSaving.value = true;
    delErrors.code = '';
    delErrors.general = '';
    try {
        await securityApi.requestAccountDeletionCode();
        delStep.value = 'code_sent';
        delForm.code = '';
        startResendCooldown();
        toast.success(t('account.profile.dangerZone.codeSent'));
    } catch (err: any) {
        if (err?.response?.status === 429) {
            delErrors.general = t('account.profile.dangerZone.rateLimited');
        } else {
            delErrors.general = t('errors.unknown');
        }
    } finally {
        delSaving.value = false;
    }
}

async function confirmDeletion() {
    delErrors.code = '';
    delErrors.general = '';
    if (!delForm.code) { delErrors.code = t('account.profile.dangerZone.required'); return; }

    delSaving.value = true;
    try {
        await securityApi.confirmAccountDeletion(delForm.code);
        toast.success(t('account.profile.dangerZone.success'));
        if (resendTimer) { clearInterval(resendTimer); resendTimer = null; }
        // Local logout: clears auth cookies and redirects to the login page.
        await Authentication.logout();
    } catch (err: any) {
        const status = err?.response?.status;
        const errorCode = err?.response?.data?.error;
        if (status === 400 && errorCode === 'invalid_code') {
            // One-strike: the code is now invalidated — surface that honestly.
            delErrors.code = t('account.profile.dangerZone.invalidCode');
        } else if (status === 409 && errorCode === 'provider_managed_user') {
            delErrors.general = t('account.profile.dangerZone.providerManaged');
        } else {
            delErrors.general = t('errors.unknown');
        }
    } finally {
        delSaving.value = false;
    }
}

onUnmounted(() => {
    if (resendTimer) clearInterval(resendTimer);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[6] || (_cache[6] = [
          _createElementVNode("div", { class: "w-8 h-8 rounded-full border-2 border-gray-200 border-t-primary-500 animate-spin" }, null, -1)
        ]))]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t('account.profile.title')), 1),
            _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t('account.profile.description')), 1)
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(AppInput, {
                modelValue: form.firstName,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.firstName) = $event)),
                label: _ctx.$t('account.profile.firstName'),
                autocomplete: "given-name"
              }, null, 8, ["modelValue", "label"]),
              _createVNode(AppInput, {
                modelValue: form.lastName,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.lastName) = $event)),
                label: _ctx.$t('account.profile.lastName'),
                autocomplete: "family-name"
              }, null, 8, ["modelValue", "label"])
            ]),
            _createVNode(AppInput, {
              modelValue: form.email,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.email) = $event)),
              label: _ctx.$t('account.profile.email'),
              type: "email",
              disabled: true,
              autocomplete: "email"
            }, null, 8, ["modelValue", "label"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createVNode(AppButton, {
                loading: saving.value,
                disabled: !hasChanged.value,
                onClick: save
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('account.profile.save')), 1)
                ]),
                _: 1
              }, 8, ["loading", "disabled"])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("h2", _hoisted_10, _toDisplayString(_ctx.$t('account.profile.language')), 1),
            _createElementVNode("p", _hoisted_11, _toDisplayString(_ctx.$t('account.profile.languageDescription')), 1),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("label", _hoisted_14, _toDisplayString(_ctx.$t('account.profile.preferredLanguage')), 1),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedLanguageId).value = $event)),
                  class: "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50",
                  disabled: languageSaving.value,
                  onChange: onLanguageChange
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(supportedLanguages.value, (lang) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: lang.id,
                      value: lang.id
                    }, _toDisplayString(lang.native_name), 9, _hoisted_16))
                  }), 128))
                ], 40, _hoisted_15), [
                  [_vModelSelect, selectedLanguageId.value]
                ])
              ]),
              (showLanguageChangedNotice.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                    _createVNode(_unref(Info), { class: "w-4 h-4 mt-0.5 flex-shrink-0" }),
                    _createElementVNode("span", null, _toDisplayString(_ctx.$t('account.profile.languageChangedNotice')), 1)
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("section", _hoisted_19, [
              (delStep.value === 'idle')
                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                    _createElementVNode("div", null, [
                      _createElementVNode("h2", _hoisted_21, _toDisplayString(_ctx.$t('account.profile.dangerZone.deleteTitle')), 1),
                      _createElementVNode("p", _hoisted_22, _toDisplayString(_ctx.$t('account.profile.dangerZone.deleteDescription')), 1)
                    ]),
                    _createElementVNode("div", _hoisted_23, [
                      _createVNode(AppButton, {
                        variant: "warning",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (delStep.value = 'warning'))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('account.profile.dangerZone.deleteAction')), 1)
                        ]),
                        _: 1
                      })
                    ])
                  ]))
                : (delStep.value === 'warning')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("div", _hoisted_24, [
                        _createVNode(_unref(AlertTriangle), { class: "w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" }),
                        _createElementVNode("div", null, [
                          _createElementVNode("h3", _hoisted_25, _toDisplayString(_ctx.$t('account.profile.dangerZone.warningTitle')), 1),
                          _createElementVNode("p", _hoisted_26, _toDisplayString(_ctx.$t('account.profile.dangerZone.warningBody')), 1)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_27, [
                        _createElementVNode("div", _hoisted_28, [
                          _createVNode(AppButton, {
                            variant: "white",
                            onClick: cancelDeletion
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('account.profile.dangerZone.cancel')), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _createElementVNode("div", _hoisted_29, [
                          _createVNode(AppButton, {
                            variant: "warning",
                            loading: delSaving.value,
                            onClick: requestDeletionCode
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('account.profile.dangerZone.warningContinue')), 1)
                            ]),
                            _: 1
                          }, 8, ["loading"])
                        ])
                      ])
                    ], 64))
                  : (delStep.value === 'code_sent')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        _createElementVNode("h2", _hoisted_30, _toDisplayString(_ctx.$t('account.profile.dangerZone.deleteTitle')), 1),
                        _createElementVNode("p", _hoisted_31, _toDisplayString(_ctx.$t('account.profile.dangerZone.codeSentInfo')), 1),
                        _createElementVNode("form", {
                          class: "space-y-3",
                          onSubmit: _withModifiers(confirmDeletion, ["prevent"])
                        }, [
                          _createElementVNode("div", _hoisted_32, [
                            _createVNode(AppInput, {
                              modelValue: delForm.code,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((delForm.code) = $event)),
                              label: _ctx.$t('account.profile.dangerZone.code'),
                              autocomplete: "one-time-code",
                              inputmode: "numeric",
                              maxlength: "6",
                              error: delErrors.code
                            }, null, 8, ["modelValue", "label", "error"])
                          ]),
                          (delErrors.general)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_33, _toDisplayString(delErrors.general), 1))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_34, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "text-sm text-primary-600 hover:text-primary-700 disabled:opacity-40 transition-colors",
                              disabled: resendCooldown.value > 0,
                              onClick: requestDeletionCode
                            }, _toDisplayString(resendCooldown.value > 0 ? _ctx.$t('account.profile.dangerZone.resendIn', { s: resendCooldown.value }) : _ctx.$t('account.profile.dangerZone.resend')), 9, _hoisted_35),
                            _createElementVNode("div", _hoisted_36, [
                              _createElementVNode("div", _hoisted_37, [
                                _createVNode(AppButton, {
                                  variant: "white",
                                  onClick: cancelDeletion
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_ctx.$t('account.profile.dangerZone.cancel')), 1)
                                  ]),
                                  _: 1
                                })
                              ]),
                              _createElementVNode("div", _hoisted_38, [
                                _createVNode(AppButton, {
                                  variant: "warning",
                                  loading: delSaving.value,
                                  onClick: confirmDeletion
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_ctx.$t('account.profile.dangerZone.confirm')), 1)
                                  ]),
                                  _: 1
                                }, 8, ["loading"])
                              ])
                            ])
                          ])
                        ], 32)
                      ], 64))
                    : _createCommentVNode("", true)
            ])
          ])
        ], 64))
  ]))
}
}

})